import { useFormik } from 'formik';

import { useLanguage } from '../../../../context/LanguageContext';
import { useNavigationStack } from '../../../../context/NavigationStackContext';
import { useThemeContext } from '../../../../context/ThemeContext';
import { useGetLanguages } from '../../../../redux/api/translationsAPI';
import {
  useGetMyDetails,
  useUpdateMyConfig,
  useUpdateUser,
} from '../../../../redux/api/users/usersApi';
import {
  AccessScope,
  PostsInteractionScope,
  PrimaryColor,
  ThemeMode,
  UserConfigKey,
} from '../../../../shared/constants';
import { Paths } from '../../../../shared/paths';
import { ILanguage } from '../../../../types/interfaces/language';

interface IUserConfigFormValues {
  scope: AccessScope | undefined;
  postsScope: PostsInteractionScope | undefined;
  commentsScope: PostsInteractionScope | undefined;
  language: ILanguage | undefined;
  theme: ThemeMode;
  primaryColor: PrimaryColor;
}

export function useUserConfigEditComponent() {
  const { data: user } = useGetMyDetails();
  const [updateUser, { isLoading: isUserLoading }] = useUpdateUser();
  const [updateMyConfig, { isLoading: isConfigLoading }] = useUpdateMyConfig();
  const { data: languages } = useGetLanguages();
  const { currentLanguage } = useLanguage();
  const { theme, primaryColor, applyVisualizationConfig } = useThemeContext();
  const { replace } = useNavigationStack();

  const formik = useFormik<IUserConfigFormValues>({
    enableReinitialize: true,
    initialValues: {
      scope: user?.accessConfig?.scope,
      postsScope: user?.accessConfig?.postsScope,
      commentsScope: user?.accessConfig?.commentsScope,
      language: languages?.find(
        (l) => l.id === (user?.languageId || currentLanguage?.id),
      ),
      theme: theme as ThemeMode,
      primaryColor: primaryColor as PrimaryColor,
    },
    onSubmit,
  });

  function onSubmit(values: IUserConfigFormValues) {
    if (!user?.id || !values.language?.id) {
      return;
    }
    updateUser({
      id: user.id,
      body: {
        languageId: values.language.id,
        accessConfig: {
          scope: values.scope,
          postsScope: values.postsScope,
          commentsScope: values.commentsScope,
        },
      },
    })
      .unwrap()
      .then(() =>
        updateMyConfig({
          config: [
            { key: UserConfigKey.theme, value: values.theme },
            { key: UserConfigKey.primaryColor, value: values.primaryColor },
          ],
        }).unwrap(),
      )
      .then(() => {
        applyVisualizationConfig({
          theme: values.theme,
          primaryColor: values.primaryColor,
        });
        replace(Paths.settings.toConfigView);
      })
      .catch((error) => {
        console.error(error);
      });
  }

  return { formik, isLoading: isUserLoading || isConfigLoading };
}
